import   React               ,
       { useState            ,
         useEffect           }  from 'react';

import   classNames             from 'classnames';
import   styles                 from './BroadcastingChannelCategoryCreation.module.scss';

import   Toggle                 from '../../widget/inputs/Toggle';
import   Window                 from '../../widget/windows/window';

import { useTypedSelector }     from '../../../store/useTypeSelector';
import { RootState }            from '../../../store/generalReducer';

import { createBroadcastingChannelCategory }  from '../../../backendServices/broadcastingManagment/api_category';
import { DUPLICATE_NAME_CODE }  from '../../../dataTypes/backendServicesManagment/types';
import { BroadcastingChannelCategory }        from '../../../dataTypes/broadcastingManagment/types';

export interface BroadcastingChannelCategoryCreationProps {
  onCreate      : (createdCategory?: BroadcastingChannelCategory) => void;
  numCategories : number;
}

export const BroadcastingChannelCategoryCreation = ({ onCreate, numCategories }: BroadcastingChannelCategoryCreationProps) => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);

  const [ name             , setName             ] = useState("");
  const [ rank             , setRank             ] = useState(1);
  const [ visible          , setVisible          ] = useState(true);
  const [ nameErrorMessage , setNameErrorMessage ] = useState("");
  const [ rankOptions      , setRankOptions      ] = useState<string[]>([]);
  const [ errorMessage     , setErrorMessage     ] = useState(<></>);
  const [ successMessage   , setSuccessMessage   ] = useState(<></>);
  const [ showWindow       , setShowWindow       ] = useState(false);
  const [ isSuccess        , setIsSuccess        ] = useState(false);
  const [ createdCategory  , setCreatedCategory  ] = useState<BroadcastingChannelCategory | undefined>(undefined);

  useEffect(() => {
    const options = Array.from({ length: numCategories + 1 }, (_, i) => (i + 1).toString());
    setRankOptions(options);
  }, [numCategories]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    setNameErrorMessage(event.target.value === '' ? "Le nom ne peut pas être vide" : "");
  };

  const handleRankChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRank(parseInt(event.target.value, 10));
  };

  const handleVisibilityChange = (newState: boolean) => {
    setVisible(newState);
  };

  const addCategory = async () => {
    if (name === '') {
      setNameErrorMessage("Le nom ne peut pas être vide");
    } else {
      setNameErrorMessage("");
      const result = await createBroadcastingChannelCategory(name, rank, visible, authentication.admin?.id);
      if (result.code === 1) {
        setSuccessMessage(<>La catégorie a été créée avec succès !</>);
        setErrorMessage(<></>);
        setIsSuccess(true);
        setCreatedCategory(result.payload);
        setShowWindow(true);
        setName('');
        setRank(1);
        setVisible(false);
      } else {
        setSuccessMessage(<></>);
        // Un nom déjà pris par une catégorie du même créateur se dit tel quel : c'est
        // le seul refus que l'utilisateur peut corriger lui-même, et le message
        // général le laissait croire à une panne.
        setErrorMessage(result.code === DUPLICATE_NAME_CODE
                        ? <>Cette catégorie existe déjà</>
                        : <>Une erreur est survenue lors de la création de la catégorie.</>);
        setIsSuccess(false);
        setCreatedCategory(undefined);
        setShowWindow(true);
      }
    }
  };

  const handleCloseWindow = () => {
    setShowWindow(false);
    if (isSuccess) { onCreate(createdCategory); }
  };

  return (
    <div className={classNames(styles.root)}>

      <div className={classNames(styles.sectionHeader)}>
        <div className={classNames(styles.sectionIcon)}>+</div>
        <span className={classNames(styles.sectionTitle)}>Ajouter une catégorie</span>
      </div>

      <div className={classNames(styles.container)}>

        {/* Nom — prend tout l'espace disponible */}
        <div className={classNames(styles.fieldGroupName)}>
          <label className={classNames(styles.fieldLabel)}>Nom</label>
          <input
            className   = {classNames(styles.fieldInput)}
            type        = "text"
            placeholder = "Ex. : Sport"
            value       = {name}
            onChange    = {handleNameChange}
          />
          {nameErrorMessage && (
            <span className={classNames(styles.errorText)}>{nameErrorMessage}</span>
          )}
        </div>

        {/* Rang */}
        <div className={classNames(styles.fieldGroup)}>
          <label className={classNames(styles.fieldLabel)}>Rang</label>
          <select
            className = {classNames(styles.fieldSelectRang)}
            value     = {rank.toString()}
            onChange  = {handleRankChange}
          >
            {rankOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>

        {/* Visible — toggle aligné avec les inputs */}
        <div className={classNames(styles.toggleGroup)}>
          <label className={classNames(styles.fieldLabel)}>Visible</label>
          <div className={classNames(styles.toggleWrapper)}>
            <Toggle
              checked  = {visible}
              form     = "switch"
              onChange = {handleVisibilityChange}
            />
          </div>
        </div>

        {/* Bouton — poussé tout à droite par margin-left:auto */}
        <button className={classNames(styles.addButton)} onClick={addCategory}>
          <span className={classNames(styles.plusIcon)}>+</span>
          Ajouter
        </button>

      </div>

      {showWindow && (
        <Window
          message                 = {isSuccess ? successMessage : errorMessage}
          messageImportance       = {isSuccess ? 'normal' : 'important'}
          onClickOnValidateButton = {handleCloseWindow}
        />
      )}

    </div>
  );
};

export default BroadcastingChannelCategoryCreation;
